import { useCallback, useEffect, useRef, useState } from "react";

export default function usePersistentState(key, defaultValue) {
  const readValue = useCallback(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === null) return defaultValue;
      return JSON.parse(stored);
    } catch (_err) {
      return defaultValue;
    }
  }, [key, defaultValue]);

  const [value, setValue] = useState(readValue);
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    const next = readValue();
    setValue((prev) => (Object.is(prev, next) ? prev : next));
  }, [readValue]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (value === undefined) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (_err) {
      // ignore storage errors (quota, privacy mode, etc.)
    }
  }, [key, value]);

  const updateValue = useCallback(
    (next) => {
      const prev = valueRef.current;
      const resolved = typeof next === "function" ? next(prev) : next;
      if (Object.is(prev, resolved)) {
        return;
      }
      valueRef.current = resolved;
      setValue(resolved);
    },
    []
  );

  return [value, updateValue];
}
